import React, { useState, useEffect, useCallback } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Badge,
  Table,
  Spinner,
} from 'reactstrap';
import moment from 'moment';
import Autocomplete from '@material-ui/lab/Autocomplete';
import TextField from '@material-ui/core/TextField';
import { useOrganizationStore } from '../../store/organization.store';
import { generateClaimCode } from '../../types/license';
import { ShareTarget } from './ShareLicenseModal';

interface LicenseField {
  label: string;
  value: string;
}

interface License {
  id: string;
  status: string;
  licenseType: string;
  dateExpires: number | null;
  dateStart: number | null;
  createdAt: number;
  clientOrgId: string | null;
  claimCode: string | null;
  jobTitle?: string;
  client?: LicenseField | null;
  agency?: LicenseField | null;
  flightingPlatform?: LicenseField[];
  territories?: LicenseField[];
  // Snapshot fields
  clientOrgName?: string;
}

interface LicenseHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  trackName: string;
  trackId: string;
  licenses: License[];
  onDeactivateLicense: (licenseId: string) => Promise<void>;
  onAddLicense: () => void;
  isLoading?: boolean;
  firebase: any;
}

const LicenseHistoryModal: React.FC<LicenseHistoryModalProps> = ({
  isOpen,
  onClose,
  trackName,
  trackId,
  licenses,
  onDeactivateLicense,
  onAddLicense,
  isLoading = false,
  firebase,
}) => {
  const [deactivatingId, setDeactivatingId] = useState<string | null>(null);
  const [confirmDeactivateId, setConfirmDeactivateId] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  // Share modal state
  const [shareLicenseId, setShareLicenseId] = useState<string | null>(null);
  const [shareTarget, setShareTarget] = useState<ShareTarget | null>(null);
  const [shareTargetSearch, setShareTargetSearch] = useState('');
  const [shareTargetOptions, setShareTargetOptions] = useState<ShareTarget[]>([]);
  const [isSearchingTargets, setIsSearchingTargets] = useState(false);
  const [isSharing, setIsSharing] = useState(false);

  // Claim link modal state
  const [showClaimLinkModal, setShowClaimLinkModal] = useState(false);
  const [claimLink, setClaimLink] = useState<string | null>(null);
  const [copySuccess, setCopySuccess] = useState(false);

  const { currentOrgId } = useOrganizationStore();

  // Search share targets with debounce
  const searchShareTargets = useCallback(
    async (searchTerm: string) => {
      if (!searchTerm || searchTerm.length < 2 || !firebase?.searchShareTargets) {
        setShareTargetOptions([]);
        return;
      }
      setIsSearchingTargets(true);
      try {
        const results = await firebase.searchShareTargets(searchTerm, currentOrgId);
        setShareTargetOptions(results);
      } catch {
        setShareTargetOptions([]);
      } finally {
        setIsSearchingTargets(false);
      }
    },
    [firebase, currentOrgId]
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      searchShareTargets(shareTargetSearch);
    }, 300);
    return () => clearTimeout(timer);
  }, [shareTargetSearch, searchShareTargets]);

  const handleCopyClaimLink = (license: License) => {
    if (!license.claimCode) return;
    const baseUrl = window.location.origin;
    const link = `${baseUrl}/claim/${license.claimCode}`;
    navigator.clipboard.writeText(link).then(() => {
      setCopiedId(license.id);
      setTimeout(() => setCopiedId(null), 2000);
    });
  };

  const handleDeactivateClick = (licenseId: string) => {
    setConfirmDeactivateId(licenseId);
  };

  const handleConfirmDeactivate = async () => {
    if (!confirmDeactivateId) return;

    setDeactivatingId(confirmDeactivateId);
    setConfirmDeactivateId(null);
    try {
      await onDeactivateLicense(confirmDeactivateId);
    } finally {
      setDeactivatingId(null);
    }
  };

  const handleCancelDeactivate = () => {
    setConfirmDeactivateId(null);
  };

  const handleShareClick = (licenseId: string) => {
    setShareLicenseId(licenseId);
    setShareTarget(null);
    setShareTargetSearch('');
    setShareTargetOptions([]);
  };

  const handleCancelShare = () => {
    setShareLicenseId(null);
    setShareTarget(null);
    setShareTargetSearch('');
    setShareTargetOptions([]);
  };

  const handleConfirmShare = async () => {
    if (!shareLicenseId || !shareTarget || !currentOrgId || !firebase) return;

    setIsSharing(true);
    try {
      if (shareTarget.hasOrg && shareTarget.linkedOrgId) {
        // Direct share
        await firebase.shareLicense(currentOrgId, trackId, shareLicenseId, {
          clientOrgId: shareTarget.linkedOrgId,
        });
      } else {
        // Generate claim code
        const code = generateClaimCode();
        await firebase.shareLicense(currentOrgId, trackId, shareLicenseId, {
          claimCode: code,
        });
        // Show claim link modal
        const baseUrl = window.location.origin;
        setClaimLink(`${baseUrl}/claim/${code}`);
        setShowClaimLinkModal(true);
      }
      handleCancelShare();
    } catch (error) {
      console.error('Error sharing license:', error);
    } finally {
      setIsSharing(false);
    }
  };

  const copyClaimLinkToClipboard = async () => {
    if (claimLink) {
      try {
        await navigator.clipboard.writeText(claimLink);
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
      } catch (err) {
        console.error('Failed to copy:', err);
      }
    }
  };

  const getShareTargetLabel = (target: ShareTarget) => {
    const typeLabel = target.type === 'org' ? 'Org' : target.type === 'client' ? 'Client' : 'Agency';
    return `${target.name} (${typeLabel})`;
  };

  // Get the license being confirmed for deactivation
  const licenseToDeactivate = confirmDeactivateId
    ? licenses.find(l => l.id === confirmDeactivateId)
    : null;

  const getStatusBadge = (license: License) => {
    const isExpired = license.dateExpires && license.dateExpires < Date.now();

    if (license.status === 'inactive' || isExpired) {
      return <Badge className="badge-inactive">Inactive</Badge>;
    }
    if (license.status === 'pending') {
      return <Badge color="warning">Pending</Badge>;
    }
    return <Badge className="badge-accent">Active</Badge>;
  };

  const getSharedInfo = (license: License) => {
    if (license.clientOrgId) {
      return license.clientOrgName || license.client?.label || 'Claimed';
    }
    if (license.claimCode) {
      return 'Link shared (unclaimed)';
    }
    return 'Not shared';
  };

  const isUnshared = (license: License) => {
    return !license.clientOrgId && !license.claimCode;
  };

  const formatDate = (timestamp: number | null) => {
    if (!timestamp) return 'Blanket';
    return moment(timestamp).format('DD/MM/YYYY');
  };

  const formatArrayField = (items: LicenseField[] | undefined, limit: number = 2) => {
    if (!items || items.length === 0) return { display: '-', tooltip: '' };

    const labels = items.map(item => item.label);

    if (labels.length <= limit) {
      return { display: labels.join(', '), tooltip: '' };
    }

    const displayed = labels.slice(0, limit).join(', ');
    const remaining = labels.length - limit;
    const fullList = labels.join(', ');

    return {
      display: `${displayed}, +${remaining} more`,
      tooltip: fullList,
    };
  };

  const isLicenseActive = (license: License) => {
    if (license.status === 'inactive') return false;
    if (license.dateExpires && license.dateExpires < Date.now()) return false;
    return true;
  };

  // Sort licenses: active first, then by createdAt desc
  const sortedLicenses = [...licenses].sort((a, b) => {
    const aActive = isLicenseActive(a);
    const bActive = isLicenseActive(b);
    if (aActive && !bActive) return -1;
    if (!aActive && bActive) return 1;
    return b.createdAt - a.createdAt;
  });

  return (
    <Modal
      isOpen={isOpen}
      toggle={onClose}
      size="lg"
      style={{
        color: '#ffffff',
        maxWidth: '1000px',
        margin: '1.75rem auto',
      }}
      contentClassName="delete-confirm-modal"
    >
      <ModalHeader
        toggle={onClose}
        style={{
          backgroundColor: 'var(--chatbot-color-2)',
          color: '#ffffff',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        }}
        close={
          <button
            className="close"
            onClick={onClose}
            style={{
              color: 'var(--accent-1-color)',
              opacity: 1,
              textShadow: 'none',
              background: 'none',
              border: 'none',
              fontSize: '1.75rem',
              fontWeight: 'normal',
              cursor: 'pointer',
            }}
          >
            &times;
          </button>
        }
      >
        <span style={{ color: '#fff' }}>License History - {trackName}</span>
      </ModalHeader>
      <ModalBody
        style={{
          backgroundColor: 'var(--chatbot-color-1)',
          color: '#ffffff',
          padding: '1.5rem',
          maxHeight: '400px',
          overflowY: 'auto',
        }}
      >
        {isLoading ? (
          <div className="text-center py-4">
            <small className="text-muted">Loading licenses...</small>
          </div>
        ) : licenses.length === 0 ? (
          <div className="text-center py-4">
            <p className="text-muted mb-2">No license history for this track.</p>
            <p className="text-muted mb-0" style={{ fontSize: '0.85rem' }}>
              Click "Add New License" below to create one.
            </p>
          </div>
        ) : (
          <Table
            dark
            responsive
            size="sm"
            style={{
              backgroundColor: 'transparent',
              marginBottom: 0,
            }}
          >
            <thead>
              <tr style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.2)' }}>
                <th style={{ border: 'none', padding: '0.5rem' }}>Status</th>
                <th style={{ border: 'none', padding: '0.5rem' }}>Expires</th>
                <th style={{ border: 'none', padding: '0.5rem' }}>Shared With</th>
                <th style={{ border: 'none', padding: '0.5rem' }}>Job Title</th>
                <th style={{ border: 'none', padding: '0.5rem' }}>Client</th>
                <th style={{ border: 'none', padding: '0.5rem' }}>Agency</th>
                <th style={{ border: 'none', padding: '0.5rem' }}>Platforms</th>
                <th style={{ border: 'none', padding: '0.5rem' }}>Territories</th>
                <th style={{ border: 'none', padding: '0.5rem', width: '50px' }}></th>
              </tr>
            </thead>
            <tbody>
              {sortedLicenses.map((license) => (
                <tr
                  key={license.id}
                  style={{
                    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                    opacity: isLicenseActive(license) ? 1 : 0.6,
                  }}
                >
                  <td style={{ border: 'none', padding: '0.75rem 0.5rem', verticalAlign: 'middle' }}>
                    {getStatusBadge(license)}
                  </td>
                  <td style={{ border: 'none', padding: '0.75rem 0.5rem', verticalAlign: 'middle' }}>
                    <small>{formatDate(license.dateExpires)}</small>
                  </td>
                  <td style={{ border: 'none', padding: '0.75rem 0.5rem', verticalAlign: 'middle' }}>
                    <small>{getSharedInfo(license)}</small>
                    {license.claimCode && !license.clientOrgId && isLicenseActive(license) && (
                      <Button
                        color="link"
                        size="sm"
                        className="p-0 ml-2"
                        onClick={() => handleCopyClaimLink(license)}
                        title="Copy claim link"
                        style={{ verticalAlign: 'middle' }}
                      >
                        <i
                          className={`tim-icons ${copiedId === license.id ? 'icon-check-2' : 'icon-single-copy-04'}`}
                          style={{
                            fontSize: '13px',
                            color: copiedId === license.id ? '#2dce89' : 'var(--accent-1-color)',
                          }}
                        ></i>
                      </Button>
                    )}
                    {isLicenseActive(license) && isUnshared(license) && (
                      <Button
                        color="link"
                        size="sm"
                        className="p-0 ml-2"
                        onClick={() => handleShareClick(license.id)}
                        title="Share license"
                        style={{ verticalAlign: 'middle' }}
                      >
                        <i
                          className="tim-icons icon-send"
                          style={{
                            fontSize: '13px',
                            color: 'var(--accent-1-color)',
                          }}
                        ></i>
                      </Button>
                    )}
                  </td>
                  <td style={{ border: 'none', padding: '0.75rem 0.5rem', verticalAlign: 'middle' }}>
                    <small className="text-muted">{license.jobTitle || '-'}</small>
                  </td>
                  <td style={{ border: 'none', padding: '0.75rem 0.5rem', verticalAlign: 'middle' }}>
                    <small className="text-muted">{license.client?.label || '-'}</small>
                  </td>
                  <td style={{ border: 'none', padding: '0.75rem 0.5rem', verticalAlign: 'middle' }}>
                    <small className="text-muted">{license.agency?.label || '-'}</small>
                  </td>
                  <td style={{ border: 'none', padding: '0.75rem 0.5rem', verticalAlign: 'middle' }}>
                    {(() => {
                      const { display, tooltip } = formatArrayField(license.flightingPlatform, 1);
                      return (
                        <small
                          className="text-muted"
                          title={tooltip}
                          style={tooltip ? { cursor: 'help' } : undefined}
                        >
                          {display}
                        </small>
                      );
                    })()}
                  </td>
                  <td style={{ border: 'none', padding: '0.75rem 0.5rem', verticalAlign: 'middle' }}>
                    {(() => {
                      const { display, tooltip } = formatArrayField(license.territories, 1);
                      return (
                        <small
                          className="text-muted"
                          title={tooltip}
                          style={tooltip ? { cursor: 'help' } : undefined}
                        >
                          {display}
                        </small>
                      );
                    })()}
                  </td>
                  <td style={{ border: 'none', padding: '0.75rem 0.5rem', verticalAlign: 'middle', textAlign: 'center' }}>
                    {isLicenseActive(license) && (
                      <Button
                        color="link"
                        size="sm"
                        className="p-0 text-white"
                        onClick={() => handleDeactivateClick(license.id)}
                        disabled={deactivatingId === license.id}
                        title="Deactivate license"
                      >
                        {deactivatingId === license.id ? (
                          <i className="tim-icons icon-refresh-02 spin"></i>
                        ) : (
                          <i className="tim-icons icon-simple-remove"></i>
                        )}
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </ModalBody>
      <ModalFooter
        style={{
          backgroundColor: 'var(--chatbot-color-2)',
          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
          justifyContent: 'space-between',
        }}
      >
        <Button
          className="btn-primary-custom btn-ripple"
          onClick={() => {
            onAddLicense();
          }}
        >
          <span style={{ position: 'relative', zIndex: 2 }}>Add New License</span>
        </Button>
        <Button
          className="btn-info-custom"
          onClick={onClose}
        >
          <span style={{ position: 'relative', zIndex: 2 }}>Close</span>
        </Button>
      </ModalFooter>

      {/* Deactivation Confirmation Modal */}
      <Modal
        isOpen={confirmDeactivateId !== null}
        toggle={handleCancelDeactivate}
        style={{
          color: '#ffffff',
          maxWidth: '450px',
          margin: '1.75rem auto',
        }}
        contentClassName="delete-confirm-modal"
      >
        <ModalHeader
          toggle={handleCancelDeactivate}
          style={{
            backgroundColor: 'var(--chatbot-color-2)',
            color: '#ffffff',
            borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          }}
          close={
            <button
              className="close"
              onClick={handleCancelDeactivate}
              style={{
                color: 'var(--accent-1-color)',
                opacity: 1,
                textShadow: 'none',
                background: 'none',
                border: 'none',
                fontSize: '1.75rem',
                fontWeight: 'normal',
                cursor: 'pointer',
              }}
            >
              &times;
            </button>
          }
        >
          <span style={{ color: '#fff' }}>Confirm Deactivation</span>
        </ModalHeader>
        <ModalBody
          style={{
            backgroundColor: 'var(--chatbot-color-1)',
            color: '#ffffff',
            padding: '1.5rem',
          }}
        >
          <p style={{ marginBottom: '1rem' }}>
            Are you sure you want to deactivate this license?
          </p>
          {licenseToDeactivate && (
            <div
              style={{
                padding: '0.75rem',
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                borderRadius: '6px',
                fontSize: '0.9rem',
              }}
            >
              <div><strong>Track:</strong> {trackName}</div>
              {licenseToDeactivate.clientOrgName && (
                <div><strong>Licensed to:</strong> {licenseToDeactivate.clientOrgName}</div>
              )}
              <div>
                <strong>Expires:</strong> {licenseToDeactivate.dateExpires
                  ? moment(licenseToDeactivate.dateExpires).format('DD/MM/YYYY')
                  : 'Blanket'}
              </div>
            </div>
          )}
          <p style={{ marginTop: '1rem', fontSize: '0.85rem', color: 'rgba(255, 255, 255, 0.6)' }}>
            This action will revoke access for the licensed organization.
          </p>
        </ModalBody>
        <ModalFooter
          style={{
            backgroundColor: 'var(--chatbot-color-2)',
            borderTop: '1px solid rgba(255, 255, 255, 0.1)',
            justifyContent: 'flex-end',
            gap: '0.5rem',
          }}
        >
          <Button
            className="btn-secondary-custom"
            onClick={handleCancelDeactivate}
          >
            <span style={{ position: 'relative', zIndex: 2 }}>Cancel</span>
          </Button>
          <Button
            className="btn-danger-custom"
            onClick={handleConfirmDeactivate}
          >
            <span style={{ position: 'relative', zIndex: 2 }}>Deactivate</span>
          </Button>
        </ModalFooter>
      </Modal>

      {/* Share License Modal */}
      <Modal
        isOpen={shareLicenseId !== null}
        toggle={handleCancelShare}
        style={{
          color: '#ffffff',
          maxWidth: '500px',
          margin: '1.75rem auto',
        }}
        contentClassName="delete-confirm-modal"
      >
        <ModalHeader
          toggle={handleCancelShare}
          style={{
            backgroundColor: 'var(--chatbot-color-2)',
            color: '#ffffff',
            borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          }}
          close={
            <button
              className="close"
              onClick={handleCancelShare}
              style={{
                color: 'var(--accent-1-color)',
                opacity: 1,
                textShadow: 'none',
                background: 'none',
                border: 'none',
                fontSize: '1.75rem',
                fontWeight: 'normal',
                cursor: 'pointer',
              }}
            >
              &times;
            </button>
          }
        >
          <span style={{ color: '#fff' }}>Share License</span>
        </ModalHeader>
        <ModalBody
          style={{
            backgroundColor: 'var(--chatbot-color-1)',
            color: '#ffffff',
            padding: '1.5rem',
          }}
        >
          <p style={{ marginBottom: '1rem', color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.9rem' }}>
            Search for an organization, client, or agency to share this license with.
          </p>

          {shareTarget ? (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '0.75rem',
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              borderRadius: '4px',
            }}>
              <div>
                <div style={{ fontWeight: 500 }}>{shareTarget.name}</div>
                <div style={{ fontSize: '0.8rem', color: 'rgba(255, 255, 255, 0.6)' }}>
                  {shareTarget.type === 'org' ? 'Organization' : shareTarget.type === 'client' ? 'Client' : 'Agency'}
                  {' - '}
                  {shareTarget.hasOrg ? (
                    <span style={{ color: 'var(--success-color, #28a745)' }}>Will share directly</span>
                  ) : (
                    <span style={{ color: 'var(--accent-1-color)' }}>Will create claim link</span>
                  )}
                </div>
              </div>
              <Button
                size="sm"
                color="link"
                onClick={() => {
                  setShareTarget(null);
                  setShareTargetSearch('');
                }}
                style={{ color: 'var(--accent-1-color)', padding: '0.25rem 0.5rem' }}
              >
                <i className="tim-icons icon-simple-remove"></i>
              </Button>
            </div>
          ) : (
            <Autocomplete
              options={shareTargetOptions}
              getOptionLabel={(option) => getShareTargetLabel(option)}
              inputValue={shareTargetSearch}
              onInputChange={(_, newValue) => setShareTargetSearch(newValue)}
              onChange={(_, newValue) => {
                setShareTarget(newValue);
                setShareTargetSearch('');
              }}
              loading={isSearchingTargets}
              noOptionsText={shareTargetSearch.length < 2 ? "Type to search..." : "No matches found"}
              style={{ width: '100%' }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  placeholder="Search organizations, clients, or agencies..."
                  size="small"
                  style={{ backgroundColor: 'rgba(255, 255, 255, 0.05)', borderRadius: '4px' }}
                  InputProps={{
                    ...params.InputProps,
                    style: { color: '#fff', fontSize: '0.9rem' },
                  }}
                />
              )}
              renderOption={(option) => (
                <div style={{ display: 'flex', flexDirection: 'column', padding: '4px 0' }}>
                  <div style={{ fontWeight: 500 }}>{option.name}</div>
                  <div style={{ fontSize: '0.75rem', opacity: 0.7 }}>
                    {option.type === 'org' ? 'Organization' : option.type === 'client' ? 'Client' : 'Agency'}
                    {' - '}
                    {option.hasOrg ? 'Direct share' : 'Creates claim link'}
                  </div>
                </div>
              )}
            />
          )}
        </ModalBody>
        <ModalFooter
          style={{
            backgroundColor: 'var(--chatbot-color-2)',
            borderTop: '1px solid rgba(255, 255, 255, 0.1)',
            justifyContent: 'flex-end',
            gap: '0.5rem',
          }}
        >
          <Button
            className="btn-secondary-custom"
            onClick={handleCancelShare}
            disabled={isSharing}
          >
            <span style={{ position: 'relative', zIndex: 2 }}>Cancel</span>
          </Button>
          <Button
            className="btn-primary-custom"
            onClick={handleConfirmShare}
            disabled={!shareTarget || isSharing}
          >
            {isSharing ? (
              <Spinner size="sm" />
            ) : (
              <span style={{ position: 'relative', zIndex: 2 }}>
                {shareTarget?.hasOrg ? 'Share' : 'Share & Get Link'}
              </span>
            )}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Claim Link Modal */}
      <Modal
        isOpen={showClaimLinkModal}
        toggle={() => setShowClaimLinkModal(false)}
        style={{
          color: '#ffffff',
          maxWidth: '500px',
          margin: '1.75rem auto',
        }}
        contentClassName="delete-confirm-modal"
      >
        <ModalHeader
          toggle={() => setShowClaimLinkModal(false)}
          style={{
            backgroundColor: 'var(--chatbot-color-2)',
            color: '#ffffff',
            borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          }}
          close={
            <button
              className="close"
              onClick={() => setShowClaimLinkModal(false)}
              style={{
                color: 'var(--accent-1-color)',
                opacity: 1,
                textShadow: 'none',
                background: 'none',
                border: 'none',
                fontSize: '1.75rem',
                fontWeight: 'normal',
                cursor: 'pointer',
              }}
            >
              &times;
            </button>
          }
        >
          <span style={{ color: '#fff' }}>License Shared - Claim Link</span>
        </ModalHeader>
        <ModalBody
          style={{
            backgroundColor: 'var(--chatbot-color-1)',
            color: '#ffffff',
            padding: '1.5rem',
          }}
        >
          <p style={{ marginBottom: '1rem' }}>
            Share this link with the recipient to allow them to claim the license:
          </p>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.75rem',
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              borderRadius: '4px',
              marginBottom: '1rem',
            }}
          >
            <input
              type="text"
              value={claimLink || ''}
              readOnly
              style={{
                flex: 1,
                backgroundColor: 'transparent',
                border: 'none',
                color: '#fff',
                fontSize: '0.9rem',
                outline: 'none',
              }}
            />
            <Button
              size="sm"
              className="btn-primary-custom"
              onClick={copyClaimLinkToClipboard}
              style={{ whiteSpace: 'nowrap' }}
            >
              {copySuccess ? 'Copied!' : 'Copy'}
            </Button>
          </div>
          <p style={{ fontSize: '0.85rem', color: 'rgba(255, 255, 255, 0.6)' }}>
            The recipient will need to sign in or create an account to claim this license.
          </p>
        </ModalBody>
        <ModalFooter
          style={{
            backgroundColor: 'var(--chatbot-color-2)',
            borderTop: '1px solid rgba(255, 255, 255, 0.1)',
            justifyContent: 'flex-end',
          }}
        >
          <Button
            className="btn-info-custom"
            onClick={() => setShowClaimLinkModal(false)}
          >
            <span style={{ position: 'relative', zIndex: 2 }}>Done</span>
          </Button>
        </ModalFooter>
      </Modal>
    </Modal>
  );
};

export default LicenseHistoryModal;
